import { useEffect, useRef } from "react";
import { useControllableValue } from "ahooks";
import { twMerge } from "tailwind-merge";
import { AspectRatioSelect } from "./AspectRatioSelect";
import { ResolutionSelect } from "./ResolutionSelect";
import { useVideoSpecsOptions } from "./useVideoSpecsOptions";
import { DEFAULT_VIDEO_SPECS_VALUE, isSameVideoSpecsValue, normalizeVideoSpecs } from "./videoSpecsRules";
import type { VideoSpecsProps, VideoSpecsValue } from "./types";

export function VideoSpecs(props: VideoSpecsProps) {
  const { disabled, className } = props;
  const [value, setValue] = useControllableValue<VideoSpecsValue>(props, {
    defaultValue: DEFAULT_VIDEO_SPECS_VALUE,
  });
  const { options, loading } = useVideoSpecsOptions();
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!options) return;
    const preferApiDefault = !initializedRef.current;
    initializedRef.current = true;
    const normalized = normalizeVideoSpecs(value, options, preferApiDefault);
    if (!isSameVideoSpecsValue(value, normalized)) {
      setValue(normalized);
    }
  }, [options, setValue, value]);

  if (!options) return null;

  return (
    <div className={twMerge("flex items-start gap-[8px]", className)}>
      {options.aspectRatioOptions.length > 0 && (
        <AspectRatioSelect
          disabled={disabled || loading}
          value={value.aspectRatio}
          onChange={(aspectRatio) => setValue({ ...value, aspectRatio })}
          options={options.aspectRatioOptions}
        />
      )}
      {options.resolutionOptions.length > 0 && (
        <ResolutionSelect
          disabled={disabled || loading}
          value={value.resolution}
          onChange={(resolution) => setValue({ ...value, resolution })}
          options={options.resolutionOptions}
        />
      )}
    </div>
  );
}
